import { useCallback, useEffect, useMemo, useState } from "react";
import { ElementItemProps } from "../types";
import { useIntl } from "react-intl";
import { useDevice } from "../../../../utils/DeviceProvider";
import { Grid, Skeleton, Typography } from "@mui/material";
import Info from "./Info";
import { ComboxElement } from "./Combox";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { TxtboxElement } from "./Txtbox";
import { RestoreButton, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";

export interface OINV1Parameters {
  index: number;
  triggerItemsMask: number;
}

interface OINV1Value {
  trigger: string;
  event: string;
  zone: string;
  route: string;
}

const OINV1 = ({ configKey, index, triggerItemsMask, disabled }: Omit<ElementItemProps<OINV1Parameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<OINV1Value | null>(null);
  const [trigger, setTrigger] = useState<string | null>(null);
  const [event, setEvent] = useState("");
  const [zone, setZone] = useState("");
  const [route, setRoute] = useState("");

  const { formatMessage } = useIntl();
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const triggerItems = useMemo(() => {
    return [
      { hu: "Nincs", en: "None" },
      { hu: "GND be NO vissza", en: "GND in NO back" },
      { hu: "+12V be NO vissza", en: "+12V in NO back" },
      { hu: "NO be GND vissza", en: "NO in GND back" },
      { hu: "NO be +12V vissza", en: "NO in +12V back" },
      { hu: "Analóg mérés", en: "Analog measurement" },
      { hu: "Wiegand Data(x)", en: "Wiegand Data(x)" },
      { hu: "Számláló", en: "Counter" },
      { hu: "CAN bus", en: "CAN bus" },
      { hu: "Szenzor tanítás", en: "Sensor learning" },
      { hu: "Gyújtás", en: "Ignition" },
      { hu: "DHT szenzor", en: "DHT sensor" },
      { hu: "Sziréna", en: "Siren" },
    ]
      .filter((_, i) => i === 0 || Math.pow(2, i - 1) & triggerItemsMask)
      .map((label, i) => ({ value: `${i}`, label: getLabel(label, locale) }));
  }, [triggerItemsMask, locale]);
  const routeItems = useMemo(() => {
    return [
      { value: "x0", label: { hu: "Nincs", en: "None" } },
      { value: "x1", label: { hu: "Commander", en: "Commander" } },
      { value: "x2", label: { hu: "Navigátor", en: "Navigator" } },
      { value: "x3", label: { hu: "Navigátor és Commander", en: "Navigator and Commander" } },
    ].map(({ value, label }) => ({ value, label: getLabel(label, locale) }));
  }, [locale]);

  const onChange = useCallback(
    ({ trigger, event, zone, route }: { trigger?: string; event?: string; zone?: string; route?: string }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (trigger !== undefined) {
        currentValue[configKey].items[configIndex].value.trigger = trigger;
      }
      if (event !== undefined) {
        const eventValue = event.padStart(4, "0");
        if (event !== eventValue) {
          setEvent(eventValue);
        }
        currentValue[configKey].items[configIndex].value.event = eventValue;
      }
      if (zone !== undefined) {
        const zoneValue = zone.padStart(3, "0");
        if (zone !== zoneValue) {
          setZone(zoneValue);
        }
        currentValue[configKey].items[configIndex].value.zone = zoneValue;
      }
      if (route !== undefined) {
        currentValue[configKey].items[configIndex].value.route = route;
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key: string }) => type === "OINV1" && key === `OINV1-${index}`) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, index]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const { trigger, event, zone, route } = currentValue[configKey].items[configIndex]?.value ?? {};
    setTrigger(trigger);
    setEvent(event);
    setZone(zone);
    setRoute(route);
  }, [active, currentValue, config, configIndex, configKey]);

  if (trigger === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 200, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 80, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 60, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 220, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "flex-end" }}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle />
      </Grid>
      <Grid item>
        <ComboxElement
          value={trigger ?? ""}
          label={formatMessage({ defaultMessage: "Trigger" })}
          items={triggerItems}
          onChange={setTrigger}
          onBlur={() => onChange({ trigger })}
          edited={saved?.trigger !== trigger}
          disabled={disabled}
          sx={{ width: 225 }}
          onContextMenu={saved && saved.trigger !== trigger ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.trigger`) : undefined}
        />
      </Grid>
      <Grid item>
        <TxtboxElement
          value={event ?? ""}
          label={formatMessage({ defaultMessage: "Event" })}
          onChange={setEvent}
          onBlur={() => onChange({ event })}
          edited={saved?.event !== event}
          hex
          maxLength={4}
          disabled={disabled}
          sx={{ width: 80 }}
          onContextMenu={saved && saved.event !== event ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.event`) : undefined}
        />
      </Grid>
      <Grid item>
        <TxtboxElement
          value={zone ?? ""}
          label={formatMessage({ defaultMessage: "Zone" })}
          onChange={setZone}
          onBlur={() => onChange({ zone })}
          edited={saved?.zone !== zone}
          hex
          maxLength={3}
          disabled={disabled}
          sx={{ width: 80 }}
          onContextMenu={saved && saved.zone !== zone ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.zone`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={route ?? ""}
          label={formatMessage({ defaultMessage: "Route", description: "OINV1" })}
          items={routeItems}
          onChange={setRoute}
          onBlur={() => onChange({ route })}
          edited={saved?.route !== route}
          disabled={disabled}
          sx={{ width: 240 }}
          onContextMenu={saved && saved.route !== route ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.route`) : undefined}
        />
      </Grid>
      {(!saved ? trigger || event || zone || route : !isEqual(saved, { trigger, event, zone, route })) ? (
        <Grid item>
          <RestoreButton path={`${configKey}.items.${configIndex}.value`} saved={saved} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default OINV1;
